const RADIUS = 16;
const TAPS = RADIUS * 2;
const MAX_CACHED_PHASES = 1024;

/**
 * Streaming mono PCM resampling with a normalized 32-tap Blackman-windowed sinc filter.
 *
 * push() retains 16 source frames of lookahead and the preceding filter history; HTTP fragment
 * boundaries have no effect on the waveform. The cutoff is 94% of the lower Nyquist frequency,
 * providing a transition band for anti-alias filtering when downsampling. This is a compact
 * speech filter, not a brick-wall filter: frequencies close to the cutoff are attenuated gradually.
 *
 * finish() extends the endpoint samples through the filter and flushes the lookahead, producing
 * exactly round(inputFrames * outputRate / inputRate) frames in total, with no added silence or
 * output delay. Same-rate input passes through unchanged. Create a new instance per utterance.
 */
export class PcmResampler {
  private samples = new Float32Array(0);
  private startFrame = 0;
  private inputFrames = 0;
  private outputFrames = 0;
  private firstSample = 0;
  private lastSample = 0;
  private ended = false;
  private readonly cutoff: number;
  private readonly phases = new Map<number, Float64Array>();

  constructor(
    private readonly inputRate: number,
    private readonly outputRate: number,
  ) {
    if (
      !Number.isSafeInteger(inputRate) ||
      inputRate <= 0 ||
      !Number.isSafeInteger(outputRate) ||
      outputRate <= 0
    )
      throw new RangeError('PCM sample rates must be positive integers.');
    this.cutoff = Math.min(1, outputRate / inputRate) * 0.94;
  }

  push(input: Float32Array): Float32Array {
    if (this.ended) throw new Error('Cannot append PCM after the resampler has finished.');
    if (!input.length) return new Float32Array(0);
    if (this.inputRate === this.outputRate) return input.slice();
    if (!this.inputFrames) this.firstSample = input[0] ?? 0;
    this.lastSample = input[input.length - 1] ?? 0;
    const joined = new Float32Array(this.samples.length + input.length);
    joined.set(this.samples);
    joined.set(input, this.samples.length);
    this.samples = joined;
    this.inputFrames += input.length;
    // A center strictly before inputFrames - RADIUS has its entire right-hand filter history.
    const readyFrames = Math.max(
      0,
      Math.min(
        Math.ceil(((this.inputFrames - RADIUS) * this.outputRate) / this.inputRate),
        Math.round((this.inputFrames * this.outputRate) / this.inputRate),
      ),
    );
    return this.render(readyFrames);
  }

  finish(): Float32Array {
    if (this.ended) return new Float32Array(0);
    this.ended = true;
    if (this.inputRate === this.outputRate || !this.inputFrames) return new Float32Array(0);
    const result = this.render(Math.round((this.inputFrames * this.outputRate) / this.inputRate));
    this.samples = new Float32Array(0);
    this.phases.clear();
    return result;
  }

  private coefficients(remainder: number) {
    const cached = this.phases.get(remainder);
    if (cached) return cached;
    const fraction = remainder / this.outputRate;
    const weights = new Float64Array(TAPS);
    let sum = 0;
    for (let tap = 0; tap < TAPS; tap++) {
      const distance = tap - (RADIUS - 1) - fraction;
      const angle = (Math.PI * distance) / RADIUS;
      const window = 0.42 + 0.5 * Math.cos(angle) + 0.08 * Math.cos(2 * angle);
      const x = Math.PI * this.cutoff * distance;
      const weight = this.cutoff * (Math.abs(x) < 1e-12 ? 1 : Math.sin(x) / x) * window;
      weights[tap] = weight;
      sum += weight;
    }
    for (let tap = 0; tap < TAPS; tap++) weights[tap] = (weights[tap] ?? 0) / sum;
    if (this.phases.size < MAX_CACHED_PHASES) this.phases.set(remainder, weights);
    return weights;
  }

  private render(targetFrames: number) {
    const output = new Float32Array(Math.max(0, targetFrames - this.outputFrames));
    for (let index = 0; index < output.length; index++) {
      // Derive phase from an integer frame count; repeated fractional additions accumulate drift.
      const position = this.outputFrames * this.inputRate;
      const center = Math.floor(position / this.outputRate);
      const weights = this.coefficients(position % this.outputRate);
      let value = 0;
      for (let tap = 0; tap < TAPS; tap++) {
        const frame = center - (RADIUS - 1) + tap;
        const sample =
          frame < 0
            ? this.firstSample
            : frame >= this.inputFrames
              ? this.lastSample
              : (this.samples[frame - this.startFrame] ?? 0);
        value += sample * (weights[tap] ?? 0);
      }
      output[index] = value;
      this.outputFrames++;
    }
    const firstNeeded = Math.max(
      0,
      Math.floor((this.outputFrames * this.inputRate) / this.outputRate) - (RADIUS - 1),
    );
    const discard = Math.min(this.samples.length, Math.max(0, firstNeeded - this.startFrame));
    // Copy the small retained history so a tiny tail cannot retain an entire HTTP response buffer.
    this.samples = this.samples.slice(discard);
    this.startFrame += discard;
    return output;
  }
}
